import React, {FC, useCallback, useEffect, useState} from 'react'
import {useFormContext, useWatch} from 'react-hook-form'
import {Typography} from '@mui/material'
import {observer} from 'mobx-react-lite'
import {InputStringStoriesWithValidation} from '@components/UI/InputStringStoriesWithValidation/InputStringStoriesWithValidation'
import RegionsPicker from '@components/UI/RegionsPicker/RegionsPicker'
import {ImageBlotProps} from '@components/TextEditor/Blots/ImageBlot/ImageBlotProps'
import {CropInput, Figure, FigureVersion} from '@graphql/types'
import {useStore} from '@stores/rootStoreContext'
import {SimpleFigure} from '@components/UI/Icons/simpleFigure'
import SimpleImageEditor from '@components/UI/ImageEditor/SimpleImageEditor/SimpleImageEditor'
import {EDITOR_CONSTS} from './Editor.consts'
import {
  DescriptionWrapper,
  SelectRegionWrapper,
  EditorWrapper,
  ImagePreviewWrapper,
  ImageTooltipWrapper,
  MainInformationWrapper,
  SimpleFigureWrapper,
  StoryInfoWrapper,
  TitleWrapper
} from './Editor.styles'
import {EditorProps} from './EditorProps'

const Editor: FC<EditorProps> = ({figureTypeSlug}) => {
  const {cropperStore} = useStore()
  const {cropper, setCropper} = cropperStore
  const [blockImage, setBlockImage] = useState<ImageBlotProps>(EDITOR_CONSTS.defaultImage)

  const [isOpenDialogImage, setIsOpenDialogImage] = useState(false)
  const {control, setValue, setError, clearErrors} = useFormContext()
  const fields = useWatch({control})
  const [figureVersion, setFigureVersion] = useState({} as FigureVersion)

  const toggleIsOpenDialogImage = useCallback(() => {
    setIsOpenDialogImage(!isOpenDialogImage)
  }, [isOpenDialogImage])

  useEffect(() => {
    // const curFigureVersion = fields.figureVersion as FigureVersion
    // curFigureVersion.figure = fields.figure
    setFigureVersion(fields.figureVersion)
    setCropper((fields.figureVersion?.crop as CropInput) || null)
    // setBlockImage({
    //   id: 'storyEdit' + value?.figure?.id || '',
    //   src: value?.primaryProcessedImage?.url || value?.primaryProcessedImage?.url || '',
    //   cropperPreviewUrl: value?.figure?.cropperPreview?.url || '',
    //   crop: {
    //     width: cropper.width,
    //     height: cropper.height,
    //     top: cropper.top,
    //     left: cropper.left
    //   },
    //   figureId: value?.figure?.id || '',
    //   figureUid: value?.figure?.uid || '',
    //   figureVersionId: value?.id || '',
    //   isFullWidth: true,
    //   isannouncingphoto: false,
    //   isoverviewblock: false,
    //   hasWatermark: false,
    //   hasDiagonalWatermark: false
    // })
  }, [fields.figureVersion, setCropper])

  const setNewFigureVersion = useCallback(
    (value: FigureVersion | undefined) => {
      if (value) {
        const newFigureVersion = {
          crop: {
            width: cropper.width,
            height: cropper.height,
            top: cropper.top,
            left: cropper.left
          },
          figure: value?.figure,
          hasDiagonalWatermark: false,
          hasWatermark: false,
          primaryProcessedImage: value?.primaryProcessedImage,
          uid: value?.uid,
          id: value?.id
        } as FigureVersion
        setFigureVersion(newFigureVersion)

        setBlockImage({
          id: 'storyEdit' + value?.figure?.id || '',
          src: value?.primaryProcessedImage?.url || value?.primaryProcessedImage?.url || '',
          cropperPreviewUrl: value?.figure?.cropperPreview?.url || '',
          crop: {
            width: cropper.width,
            height: cropper.height,
            top: cropper.top,
            left: cropper.left
          },
          figureId: value?.figure?.id || '',
          figureUid: value?.figure?.uid || '',
          figureVersionId: value?.id || '',
          isFullWidth: true,
          isannouncingphoto: false,
          isoverviewblock: false,
          hasWatermark: false,
          hasDiagonalWatermark: false
        })

        setValue('figureVersion', newFigureVersion)
        setValue('figure', value?.figure)
      }
    },
    [cropper, setValue]
  )

  const [titleErrorText, setTitleErrorText] = useState('')
  const [descriptionErrorText, setDescriptionErrorText] = useState('')

  useEffect(() => {
    if (titleErrorText.length > 0) {
      setError('title', {message: EDITOR_CONSTS.inStoryTitle})
      return
    }
    clearErrors('title')
  }, [fields.title, titleErrorText, setError, clearErrors])

  useEffect(() => {
    if (descriptionErrorText.length > 0) {
      setError('description', {message: EDITOR_CONSTS.inStoryDescription})
      return
    }
    clearErrors('description')
  }, [fields.description, descriptionErrorText, setError, clearErrors])
  return (
    <>
      <EditorWrapper>
        <MainInformationWrapper>
          <ImagePreviewWrapper>
            <div id='story-edit-image' onClick={toggleIsOpenDialogImage}>
              {fields.figureVersion?.primaryProcessedImage?.url ||
              fields.figureVersion?.figure?.latestVersion?.primaryProcessedImage?.url ? (
                <figure {...blockImage}>
                  {(fields.figureVersion?.primaryProcessedImage?.url ||
                    fields.figureVersion?.figure?.latestVersion?.primaryProcessedImage?.url) && (
                    <img
                      className={'change-item'}
                      draggable='false'
                      src={'/changeItem.jpg'}
                      onClick={toggleIsOpenDialogImage}
                    />
                  )}
                  <img
                    className='story-edit-image-preview'
                    src={
                      fields.figureVersion?.primaryProcessedImage?.url ||
                      fields.figureVersion?.figure?.latestVersion?.primaryProcessedImage?.url ||
                      '/noPhoto.jpg'
                    }
                    draggable='false'
                  />
                </figure>
              ) : (
                <SimpleFigureWrapper onClick={toggleIsOpenDialogImage}>
                  <SimpleFigure
                    width='27px'
                    height='24px'
                    viewBox='0 0 48 48'
                    color='primary'
                    style={{
                      margin: '6px 4.5px'
                    }}
                  />
                  <ImageTooltipWrapper>
                    <Typography variant='text16M'>{EDITOR_CONSTS.imageTooltipMsg}</Typography>
                  </ImageTooltipWrapper>
                </SimpleFigureWrapper>
              )}
            </div>
          </ImagePreviewWrapper>
          <StoryInfoWrapper>
            <TitleWrapper>
              <InputStringStoriesWithValidation
                id={'story-edit-title'}
                title={EDITOR_CONSTS.titleLabelMsg}
                defaultValue={fields.title}
                setValue={val => {
                  setValue('title', val)
                }}
                setErrorText={setTitleErrorText}
                showTitleLength={false}
                maxLength={EDITOR_CONSTS.maxTitleLength}
                width={'100%'}
                required={true}
                maxRows={3}
              />
            </TitleWrapper>
            <DescriptionWrapper>
              <InputStringStoriesWithValidation
                id={'story-edit-description'}
                title={EDITOR_CONSTS.descriptionLabelMsg}
                defaultValue={fields.description}
                setValue={val => {
                  setValue('description', val)
                }}
                setErrorText={setDescriptionErrorText}
                showTitleLength={false}
                maxLength={EDITOR_CONSTS.maxDescriptionLength}
                width={'100%'}
                required={true}
                maxRows={11}
              />
            </DescriptionWrapper>
            <SelectRegionWrapper>
              <RegionsPicker
                label={EDITOR_CONSTS.regionLabelMsg}
                handleChangeRegion={(newRegions: { id: string }[]) => {
                setValue('regionUids', newRegions ? newRegions.map((region) => region.id) : []);
                }}
                selectedRegionUids={fields.regionUids || []}
                width="100%"
              />
            </SelectRegionWrapper>
          </StoryInfoWrapper>
        </MainInformationWrapper>
        <SimpleImageEditor
          figureVersion={figureVersion}
          open={isOpenDialogImage}
          setIsModalOpen={setIsOpenDialogImage}
          setFigureVersion={setNewFigureVersion}
          figureTypeSlug={'story'}
        />
      </EditorWrapper>
    </>
  )
}

export default observer(Editor)
